import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { ArrowLeft, Plus, Loader2, Sparkles, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter-171 — Admin AI Talent management.
 * Admin-only seeding + curation of AI ambassadors / virtual creators.
 */
const AdminAITalent = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', bio: '', avatar_url: '',
    managed_by: 'ievntur', creator_email: '',
    skills: '', services: '',
    style: 'realistic', languages: '', industries: '', content_formats: '',
    base_package_price: '', monthly_retainer_price: '',
    turnaround_days: '', available_for_hybrid: true,
    spokesperson_style: false, ugc_style: false, livestream_capable: false,
    multilingual_capable: false,
    licensing_terms: '', revision_limit: '',
    package_tiers: [],
  });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await api.post('/admin/ai-talent/upload-photo', { image_base64: dataUrl });
      setForm(f => ({ ...f, avatar_url: r.data.url }));
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Photo upload failed');
    } finally { setUploadingPhoto(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/ai-talent');
      setList(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load AI talent');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.first_name) {
      toast.error('First name is required');
      return;
    }
    if (form.managed_by === 'creator' && !form.creator_email.trim()) {
      toast.error('Creator email is required for independently-managed avatars');
      return;
    }
    setCreating(true);
    try {
      const csv = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || null,
        managed_by: form.managed_by,
        creator_email: form.managed_by === 'creator' ? form.creator_email.trim() : null,
        skills: csv(form.skills),
        services: csv(form.services),
        bio: form.bio,
        avatar_url: form.avatar_url || null,
        style: form.style,
        languages: csv(form.languages),
        industries: csv(form.industries),
        content_formats: csv(form.content_formats),
        base_package_price: form.base_package_price ? parseFloat(form.base_package_price) : null,
        monthly_retainer_price: form.monthly_retainer_price ? parseFloat(form.monthly_retainer_price) : null,
        turnaround_days: form.turnaround_days ? parseInt(form.turnaround_days, 10) : null,
        available_for_hybrid: !!form.available_for_hybrid,
        spokesperson_style: !!form.spokesperson_style,
        ugc_style: !!form.ugc_style,
        livestream_capable: !!form.livestream_capable,
        multilingual_capable: !!form.multilingual_capable,
        licensing_terms: form.licensing_terms || null,
        revision_limit: form.revision_limit !== '' ? parseInt(form.revision_limit, 10) : null,
        package_tiers: (form.package_tiers || [])
          .filter(t => t.name || t.price)
          .map(t => ({
            name: t.name || '',
            price: t.price ? parseFloat(t.price) : null,
            description: t.description || '',
            deliverables: (t.deliverables || '').split(',').map(d => d.trim()).filter(Boolean),
            turnaround_days: t.turnaround_days ? parseInt(t.turnaround_days, 10) : null,
          })),
      };
      if (editingId) {
        await api.patch(`/admin/ai-talent/${editingId}`, payload);
        toast.success('AI talent updated');
      } else {
        await api.post('/admin/ai-talent', payload);
        toast.success('AI talent created & published to Discovery');
      }
      setShowCreate(false);
      setEditingId(null);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editingId ? 'Update failed' : 'Create failed'));
    } finally { setCreating(false); }
  };

  const resetForm = () => setForm({
    first_name: '', last_name: '', email: '', bio: '', avatar_url: '',
    managed_by: 'ievntur', creator_email: '',
    skills: '', services: '',
    style: 'realistic', languages: '', industries: '', content_formats: '',
    base_package_price: '', monthly_retainer_price: '',
    turnaround_days: '', available_for_hybrid: true,
    spokesperson_style: false, ugc_style: false, livestream_capable: false,
    multilingual_capable: false,
    licensing_terms: '', revision_limit: '',
    package_tiers: [],
  });

  const openCreate = () => { setEditingId(null); resetForm(); setShowCreate(true); };

  const openEdit = async (t) => {
    setEditingId(t.user_id);
    const csvJoin = (a) => Array.isArray(a) ? a.join(', ') : (a || '');
    try {
      const r = await api.get(`/ai-talent/${t.user_id}`);
      const d = r.data || {};
      setForm({
        first_name: d.first_name || t.first_name || '',
        last_name: d.last_name || t.last_name || '',
        email: '', bio: d.bio || t.bio || '',
        avatar_url: d.avatar_url || d.profile_photo_url || t.avatar_url || '',
        managed_by: t.managed_by || 'ievntur',
        creator_email: t.creator_email || '',
        skills: csvJoin(t.skills), services: csvJoin(t.services),
        style: d.style || 'realistic',
        languages: csvJoin(d.languages), industries: csvJoin(d.industries),
        content_formats: csvJoin(d.content_formats),
        base_package_price: d.base_package_price ?? '',
        monthly_retainer_price: d.monthly_retainer_price ?? '',
        turnaround_days: d.turnaround_days ?? '',
        available_for_hybrid: d.available_for_hybrid !== false,
        spokesperson_style: !!d.spokesperson_style, ugc_style: !!d.ugc_style,
        livestream_capable: !!d.livestream_capable, multilingual_capable: !!d.multilingual_capable,
        licensing_terms: d.licensing_terms || '',
        revision_limit: d.revision_limit ?? '',
        package_tiers: (d.package_tiers || []).map(x => ({
          name: x.name || '', price: x.price ?? '', description: x.description || '',
          deliverables: Array.isArray(x.deliverables) ? x.deliverables.join(', ') : '',
          turnaround_days: x.turnaround_days ?? '',
        })),
      });
    } catch (_) {
      resetForm();
      setForm(f => ({ ...f, first_name: t.first_name || '', last_name: t.last_name || '', bio: t.bio || '', avatar_url: t.avatar_url || '' }));
    }
    setShowCreate(true);
  };

  const togglePublish = async (t) => {
    try {
      await api.patch(`/admin/ai-talent/${t.user_id}/publish`, { published: !t.published });
      toast.success(t.published ? 'Unpublished' : 'Published to Discovery');
      await load();
    } catch (e) {
      toast.error('Failed to update publish status');
    }
  };

  const archive = async (uid) => {
    if (!window.confirm('Archive this AI talent? They will no longer appear in Discover.')) return;
    try {
      await api.delete(`/admin/ai-talent/${uid}`);
      toast.success('Archived');
      await load();
    } catch (e) {
      toast.error('Archive failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-ai-talent-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-500" />
              AI Talent
            </h1>
            <Badge variant="outline" data-testid="ai-count">{list.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/avatar-studio')} data-testid="avatar-studio-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Custom Avatar Studio
            </Button>
            <Button
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white"
              onClick={openCreate}
              data-testid="create-ai-talent-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add AI Avatar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" /></div>
        ) : list.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">No AI talent yet. Click "Create AI Talent" to seed your first virtual ambassador.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-talent-list">
            {list.map(t => (
              <Card key={t.user_id} className="overflow-hidden" data-testid={`ai-row-${t.user_id}`}>
                <div className="aspect-square bg-gradient-to-br from-violet-100 to-fuchsia-100 relative">
                  {t.avatar_url
                    ? <img src={t.avatar_url} alt="" className="w-full h-full object-cover" />
                    : <div className="absolute inset-0 flex items-center justify-center text-fuchsia-400"><Sparkles className="w-10 h-10" /></div>}
                  <Badge className="absolute top-2 left-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-0 text-[9px] gap-1">
                    <Sparkles className="w-2.5 h-2.5" /> AI Avatar · Virtual
                  </Badge>
                  {t.is_archived
                    ? <Badge className="absolute top-2 right-2 bg-rose-500 text-white">Archived</Badge>
                    : <Badge className={`absolute top-2 right-2 border-0 ${t.published ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}`}>{t.published ? 'Published' : 'Unpublished'}</Badge>}
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{t.first_name} {t.last_name}</h3>
                    {t.style && <Badge variant="outline" className="text-[10px] capitalize">{t.style}</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 truncate mb-1">{t.bio}</p>
                  <p className="text-[11px] text-violet-600 truncate mb-2" data-testid={`booking-route-${t.user_id}`}>Booking → {t.booking_email}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-gray-500">{t.base_package_price ? `From $${t.base_package_price}` : 'Pricing TBD'}</span>
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => togglePublish(t)}
                        className={`text-[11px] px-2 py-0.5 rounded-full border ${t.published ? 'border-slate-300 text-slate-600 hover:bg-slate-50' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                        data-testid={`publish-toggle-${t.user_id}`}>
                        {t.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button type="button" onClick={() => openEdit(t)} className="text-cyan-600 hover:text-cyan-800" data-testid={`edit-${t.user_id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => archive(t.user_id)} className="text-rose-500 hover:text-rose-700" data-testid={`archive-${t.user_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) { setEditingId(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-500" /> {editingId ? 'Edit AI Avatar' : 'Add AI Avatar'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name *</Label>
              <Input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} data-testid="ai-form-first-name" />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} />
            </div>
            {!editingId && (
              <div className="col-span-2">
                <Label>Email (optional — internal reference only, never used for login)</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="Leave blank to auto-generate" data-testid="ai-form-email" />
              </div>
            )}

            {/* Booking routing — where client inquiries for this avatar are sent */}
            <div className="col-span-2 grid grid-cols-2 gap-3 border border-violet-100 rounded-lg p-3 bg-violet-50/40">
              <div className={form.managed_by === 'creator' ? '' : 'col-span-2'}>
                <Label className="text-violet-800 font-semibold">Booking managed by</Label>
                <select
                  className="w-full border border-gray-200 rounded h-9 px-2 text-sm"
                  value={form.managed_by}
                  onChange={e => setForm(f => ({...f, managed_by: e.target.value}))}
                  data-testid="ai-form-managed-by"
                >
                  <option value="ievntur">iEvntür-managed (inquiries → gigs@ievntur.com)</option>
                  <option value="creator">Independent creator (inquiries → creator email)</option>
                </select>
              </div>
              {form.managed_by === 'creator' && (
                <div>
                  <Label>Creator booking email *</Label>
                  <Input type="email" value={form.creator_email} onChange={e => setForm(f => ({...f, creator_email: e.target.value}))} placeholder="creator@example.com" data-testid="ai-form-creator-email" />
                </div>
              )}
            </div>

            {/* Profile photo upload */}
            <div className="col-span-2">
              <Label>Profile photo</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center shrink-0 border border-violet-100">
                  {form.avatar_url
                    ? <img src={form.avatar_url} alt="preview" className="w-full h-full object-cover" data-testid="ai-form-photo-preview" />
                    : <Sparkles className="w-6 h-6 text-fuchsia-300" />}
                </div>
                <div className="flex-1 space-y-1.5">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => uploadPhoto(e.target.files?.[0])}
                    className="block w-full text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-violet-500 file:text-white file:text-xs hover:file:bg-violet-600"
                    data-testid="ai-form-photo-input"
                  />
                  <Input value={form.avatar_url} onChange={e => setForm(f => ({...f, avatar_url: e.target.value}))} placeholder="or paste an image URL" className="h-8 text-xs" data-testid="ai-form-avatar-url" />
                  {uploadingPhoto && <p className="text-[11px] text-violet-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</p>}
                </div>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Bio</Label>
              <Textarea rows={2} value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} />
            </div>
            <div className="col-span-2">
              <Label>Skills (comma-separated)</Label>
              <Input value={form.skills} onChange={e => setForm(f => ({...f, skills: e.target.value}))} placeholder="on-camera hosting, product demo, voiceover" data-testid="ai-form-skills" />
            </div>
            <div className="col-span-2">
              <Label>Services (comma-separated)</Label>
              <Input value={form.services} onChange={e => setForm(f => ({...f, services: e.target.value}))} placeholder="UGC content, social spots, event hosting" data-testid="ai-form-services" />
            </div>
            <div>
              <Label>Style</Label>
              <select className="w-full border border-gray-200 rounded h-9 px-2 text-sm" value={form.style} onChange={e => setForm(f => ({...f, style: e.target.value}))}>
                <option value="realistic">Realistic</option>
                <option value="stylized">Stylized</option>
                <option value="animated">Animated</option>
              </select>
            </div>
            <div>
              <Label>Turnaround (days)</Label>
              <Input type="number" value={form.turnaround_days} onChange={e => setForm(f => ({...f, turnaround_days: e.target.value}))} />
            </div>
            <div className="col-span-2">
              <Label>Languages (comma-separated)</Label>
              <Input value={form.languages} onChange={e => setForm(f => ({...f, languages: e.target.value}))} placeholder="en, es, fr" />
            </div>
            <div className="col-span-2">
              <Label>Industries (comma-separated)</Label>
              <Input value={form.industries} onChange={e => setForm(f => ({...f, industries: e.target.value}))} placeholder="beauty, tech, retail" />
            </div>
            <div className="col-span-2">
              <Label>Content formats (comma-separated)</Label>
              <Input value={form.content_formats} onChange={e => setForm(f => ({...f, content_formats: e.target.value}))} placeholder="ugc, spokesperson, livestream" />
            </div>
            <div>
              <Label>Base package $</Label>
              <Input type="number" value={form.base_package_price} onChange={e => setForm(f => ({...f, base_package_price: e.target.value}))} data-testid="ai-form-base-package" />
            </div>
            <div>
              <Label>Monthly retainer $</Label>
              <Input type="number" value={form.monthly_retainer_price} onChange={e => setForm(f => ({...f, monthly_retainer_price: e.target.value}))} data-testid="ai-form-retainer" />
            </div>
            <div>
              <Label>Revisions included</Label>
              <Input type="number" value={form.revision_limit} onChange={e => setForm(f => ({...f, revision_limit: e.target.value}))} placeholder="e.g. 1" data-testid="ai-form-revision-limit" />
            </div>
            <div className="col-span-2">
              <Label>Licensing terms</Label>
              <Textarea rows={2} value={form.licensing_terms} onChange={e => setForm(f => ({...f, licensing_terms: e.target.value}))} placeholder="e.g. 30-day campaign use included; perpetual license +$500." data-testid="ai-form-licensing" />
            </div>

            {/* Iter-174 — dynamic package tier editor */}
            <div className="col-span-2 space-y-2 border border-violet-100 rounded-lg p-3 bg-violet-50/40">
              <div className="flex items-center justify-between">
                <Label className="text-violet-800 font-semibold">Package tiers</Label>
                <Button type="button" size="sm" variant="outline" className="border-violet-300 text-violet-700"
                  onClick={() => setForm(f => ({ ...f, package_tiers: [...(f.package_tiers || []),
                    { name: '', price: '', description: '', deliverables: '', turnaround_days: '' }] }))}
                  data-testid="ai-form-add-tier">+ Add tier</Button>
              </div>
              {(form.package_tiers || []).length === 0 && (
                <p className="text-xs text-violet-700/70">No tiers yet — add Starter, Bundle, Custom etc.</p>
              )}
              {(form.package_tiers || []).map((tier, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-white p-2 rounded border border-violet-100" data-testid={`ai-form-tier-${idx}`}>
                  <Input className="col-span-3" placeholder="Tier name (e.g. Starter)" value={tier.name}
                    onChange={e => setForm(f => {
                      const next = [...f.package_tiers]; next[idx] = { ...next[idx], name: e.target.value }; return { ...f, package_tiers: next };
                    })} />
                  <Input className="col-span-2" type="number" placeholder="Price" value={tier.price}
                    onChange={e => setForm(f => {
                      const next = [...f.package_tiers]; next[idx] = { ...next[idx], price: e.target.value }; return { ...f, package_tiers: next };
                    })} />
                  <Input className="col-span-3" placeholder="Description" value={tier.description}
                    onChange={e => setForm(f => {
                      const next = [...f.package_tiers]; next[idx] = { ...next[idx], description: e.target.value }; return { ...f, package_tiers: next };
                    })} />
                  <Input className="col-span-2" placeholder="Deliverables (csv)" value={tier.deliverables}
                    onChange={e => setForm(f => {
                      const next = [...f.package_tiers]; next[idx] = { ...next[idx], deliverables: e.target.value }; return { ...f, package_tiers: next };
                    })} />
                  <Input className="col-span-1" type="number" placeholder="Days" value={tier.turnaround_days}
                    onChange={e => setForm(f => {
                      const next = [...f.package_tiers]; next[idx] = { ...next[idx], turnaround_days: e.target.value }; return { ...f, package_tiers: next };
                    })} />
                  <Button type="button" size="sm" variant="ghost" className="col-span-1 text-rose-600"
                    onClick={() => setForm(f => ({ ...f, package_tiers: f.package_tiers.filter((_, i) => i !== idx) }))}
                    data-testid={`ai-form-remove-tier-${idx}`}>×</Button>
                </div>
              ))}
            </div>

            <div className="col-span-2 flex flex-wrap gap-3 mt-1">
              {[
                ['available_for_hybrid', 'Available for hybrid campaigns'],
                ['multilingual_capable', 'Multilingual capable'],
                ['spokesperson_style',  'Spokesperson style'],
                ['ugc_style',           'UGC style'],
                ['livestream_capable',  'Livestream capable'],
              ].map(([k, label]) => (
                <label key={k} className="text-sm flex items-center gap-1.5">
                  <input type="checkbox" checked={!!form[k]} onChange={e => setForm(f => ({...f, [k]: e.target.checked}))} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingId(null); resetForm(); }} disabled={creating}>Cancel</Button>
            <Button onClick={save} disabled={creating} className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" data-testid="ai-form-save">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAITalent;
